import store from '@/store/store';
import ApiService from '@/services/api.service';
import { CONNECT } from '@/app.config';
import DemoService from '@/services/demo.service';
import { hasProp } from '@/utils/validate.utils';

const UsersService = {
  /**
   * Devuelve los usuarios vinculados a una instalación
   *
   * @param {String} installationID - Identificador de la instalación
   * @return {Installation} - Datos de una instalación con usuarios
   */
  async getUsers(installationID) {
    // Check demo
    if (store.getters['globalModule/isDemo']) {
      return DemoService.getUsers(installationID);
    }

    // Request
    const resource = `installations/allUsers/${installationID}/${CONNECT.SCOPE}`;
    const response = await ApiService.get(resource);

    // Genera un modelo
    const installation = {};
    installation.id = installationID;
    installation.name = response.data.name;
    installation.users = {};
    installation.pendingUsers = {};

    response.data.users.forEach(user => {
      installation.users[user._id] = user;
    });

    if (hasProp(response.data, 'pendingUsers')) {
      response.data.pendingUsers.forEach(user => {
        installation.pendingUsers[user._id] = user;
      });
    }

    // Response
    return installation;
  },

  /**
   * Invita a un usuario a utilizar la instalación
   *
   * @param {Object} data - Datos del usuario
   * @return {String} - Devuelve el email del usuario
   * @throws {badParams} - Los datos enviados no son válidos
   * @throws {userNotConfirmed} - El usuario invitado existe pero no ha validado su email
   * @throws {installationAlreadyAdded} - El usuario ya es un usuario de la instalación
   */
  async addUserAuthorization(data) {
    // Request
    if (!store.getters['globalModule/isDemo']) {
      const resource = `installations/user/${CONNECT.SCOPE}`;
      await ApiService.post(resource, data);
    }

    return data.email;
  },

  /**
   * Edita los permisos de un usuario
   *
   * @param {Object} data - Datos del usuario
   * @return {String} - Devuelve el email del usuario
   * @throws {badParams} - Los datos enviados no son válidos
   */
  async editUserAuthorization(data) {
    // Request
    if (!store.getters['globalModule/isDemo']) {
      const resource = `installations/user/${CONNECT.SCOPE}`;
      await ApiService.patch(resource, data);
    }

    return data.email;
  },

  /**
   * Elimina a un usuario de una instalación
   *
   * @param {Object} data - Datos del usuario
   * @return {String} - Devuelve el email del usuario
   * @throws {badParams} - Los datos enviados no son válidos
   */
  async deleteUserAuthorization(data) {
    // Request
    if (!store.getters['globalModule/isDemo']) {
      const resource = `installations/user/${CONNECT.SCOPE}`;
      await ApiService.delete(resource, data);
    }

    return data.email;
  },

  /**
   * Elimina a un usuario no confirmado de una instalación
   *
   * @param {Object} data - Datos del usuario
   * @return {String} - Devuelve el email del usuario
   * @throws {badParams} - Los datos enviados no son válidos
   */
  async deleteUserInvited(data) {
    const installationID = data.installationID;
    const userID = data.userID;

    // Request
    if (!store.getters['globalModule/isDemo']) {
      const resource = `users/invitation/${installationID}/${userID}/${CONNECT.SCOPE}`;
      await ApiService.delete(resource);
    }

    return data.userID;
  },
};

export default UsersService;
